import store from '~/store';
import { machineRepository } from '~/data/repositories';
import { fileLocal } from '~/data/local';
import { connectivityService } from '~/data/connectivity/ConnectivityService';
import { syncQueue } from '~/data/sync/SyncQueue';
import { SyncQueueRow } from '~/data/sync/syncQueueTypes';
import { getSyncHandler } from '~/data/sync/handlers';
import { notifyPeriodicCheckQueueChanged } from '~/data/sync/periodicCheckQueue';
import { classifySyncItemError } from '~/data/sync/syncItemError';
import {
  getSyncProgressState,
  includeAdditionalWaitingItems,
  markDone,
  markFailed,
  markFinished,
  markPaused,
  markUploading,
  resumeRun,
  startRun,
  syncProgressRunItemsFromQueueRows,
} from '~/data/sync/syncProgressState';
import { syncResponse } from '~/types';

/** First attempt plus two retries for 5xx. */
const MAX_SERVER_ATTEMPTS = 3;
const SERVER_RETRY_DELAY_MS = 500;

export type DrainOptions = {
  /** Process items that {@link retryFailed} moved back to waiting. */
  failedOnly?: boolean;
};

type RowOutcome =
  | {
      kind: 'success';
      filePaths: string[];
      websocket?: Partial<syncResponse>;
      machineIdToRefresh?: string;
    }
  | { kind: 'failed' }
  | { kind: 'network' }
  | { kind: 'skipped' };

function mergeSyncResponse(target: syncResponse, partial?: Partial<syncResponse>): void {
  if (!partial) {
    return;
  }
  if (partial.issueIds?.length) {
    target.issueIds = [...(target.issueIds || []), ...partial.issueIds];
  }
  if (partial.updatedIssuesDetails?.length) {
    target.updatedIssuesDetails = [
      ...(target.updatedIssuesDetails || []),
      ...partial.updatedIssuesDetails,
    ];
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export class SyncManager {
  private isSyncing = false;

  isDrainInProgress(): boolean {
    return this.isSyncing;
  }

  async drain(options: DrainOptions = {}): Promise<syncResponse | void> {
    if (this.isSyncing) {
      return;
    }
    this.isSyncing = true;

    try {
      return await this.runDrain(options);
    } catch (e) {
      console.error('[SyncManager] drain failed', e);
      if (getSyncProgressState().status === 'syncing') {
        markPaused();
      }
      throw e;
    } finally {
      this.isSyncing = false;
      notifyPeriodicCheckQueueChanged();
    }
  }

  private async runDrain(options: DrainOptions): Promise<syncResponse | void> {
    const userId = store.getState().user.data?.id;
    if (!userId) {
      if (getSyncProgressState().status === 'syncing') {
        markPaused();
      }
      return;
    }

    const rows = await this.dropMalformedRows(await syncQueue.listByUserId(Number(userId)));
    if (!rows.length && getSyncProgressState().status === 'idle') {
      return;
    }

    const ready = await this.prepareProgress(rows, options.failedOnly === true);
    if (!ready) {
      return;
    }

    const waitingIds = new Set(
      getSyncProgressState()
        .items.filter((item) => item.status === 'waiting')
        .map((item) => item.id)
    );
    const workRows = rows.filter((row) => waitingIds.has(row.id));

    const aggregated: syncResponse = { issueIds: [], updatedIssuesDetails: [] };
    const currentMachineId = store.getState().machine.data.id;
    let reloadCurrentMachine = false;
    let paused = false;

    for (const row of workRows) {
      if (!connectivityService.isOnline()) {
        markPaused();
        paused = true;
        break;
      }

      markUploading(row.id);
      const outcome = await this.processRowWithRetries(row);

      if (outcome.kind === 'network') {
        markPaused();
        paused = true;
        break;
      }
      if (outcome.kind === 'failed') {
        markFailed(row.id);
        continue;
      }
      if (outcome.kind === 'skipped') {
        markDone(row.id);
        continue;
      }
      if (outcome.kind !== 'success') {
        continue;
      }

      markDone(row.id);
      try {
        await syncQueue.removeById(row.id);
        if (outcome.filePaths.length) {
          await fileLocal.removeFiles(outcome.filePaths);
        }
      } catch (cleanupError) {
        console.error(`[SyncManager] cleanup failed for queue id=${row.id}`, cleanupError);
      }
      mergeSyncResponse(aggregated, outcome.websocket);
      if (outcome.machineIdToRefresh && currentMachineId) {
        const refreshId = String(outcome.machineIdToRefresh);
        const currentMachine = store.getState().machine.data;
        const refreshesOpenMachine =
          refreshId === String(currentMachineId) ||
          !!currentMachine.linkedMachines?.some((linked) => String(linked.id) === refreshId);
        if (refreshesOpenMachine) {
          reloadCurrentMachine = true;
        }
      }
    }

    if (!paused) {
      markFinished();
    }

    if (reloadCurrentMachine && currentMachineId) {
      const machine = await machineRepository.getMachine(currentMachineId);
      if (machine) {
        store.getActions().machine.fetchedMachine(machine);
      }
    }

    const hasPayload =
      (aggregated.issueIds && aggregated.issueIds.length > 0) ||
      (aggregated.updatedIssuesDetails && aggregated.updatedIssuesDetails.length > 0);

    return hasPayload ? aggregated : undefined;
  }

  /**
   * `idle` starts a run. `paused` or a leftover `syncing` state resumes unfinished
   * items. A finished run only picks up queue rows that were not part of that run.
   */
  private async prepareProgress(rows: SyncQueueRow[], failedOnly: boolean): Promise<boolean> {
    const progress = getSyncProgressState();
    const runItems = await syncProgressRunItemsFromQueueRows(rows);

    if (failedOnly) {
      return progress.status === 'syncing';
    }

    if (progress.status === 'syncing') {
      includeAdditionalWaitingItems(runItems);
      return true;
    }

    if (progress.status === 'paused') {
      resumeRun();
      includeAdditionalWaitingItems(runItems);
      return getSyncProgressState().status === 'syncing';
    }

    if (progress.status === 'idle') {
      if (!runItems.length) {
        return false;
      }
      startRun(runItems);
      return getSyncProgressState().status === 'syncing';
    }

    if (progress.status === 'success' || progress.status === 'partial_failure') {
      includeAdditionalWaitingItems(runItems);
      const next = getSyncProgressState();
      return next.status === 'syncing' && next.items.some((item) => item.status === 'waiting');
    }

    return false;
  }

  private async dropMalformedRows(rows: SyncQueueRow[]): Promise<SyncQueueRow[]> {
    const valid: SyncQueueRow[] = [];
    for (const row of rows) {
      try {
        JSON.parse(row.data);
        valid.push(row);
      } catch (e) {
        console.error(`[SyncManager] malformed queue row id=${row.id}`, e);
        await syncQueue.removeById(row.id);
      }
    }
    return valid;
  }

  private async processRowWithRetries(row: SyncQueueRow): Promise<RowOutcome> {
    let requestData: Record<string, unknown>;
    try {
      requestData = JSON.parse(row.data) as Record<string, unknown>;
    } catch (e) {
      console.error(`[SyncManager] malformed queue row id=${row.id}`, e);
      await syncQueue.removeById(row.id);
      return { kind: 'skipped' };
    }

    const handler = getSyncHandler(row.typeOfData);
    if (!handler) {
      console.warn(`[SyncManager] no handler for type=${row.typeOfData} id=${row.id}`);
      return { kind: 'failed' };
    }

    for (let attempt = 1; attempt <= MAX_SERVER_ATTEMPTS; attempt++) {
      try {
        const outcome = await handler.process(row, requestData);
        if (!outcome.removeQueueRow) {
          return { kind: 'failed' };
        }
        return {
          kind: 'success',
          filePaths: outcome.localFilePathsToDelete,
          websocket: outcome.websocket,
          machineIdToRefresh: outcome.machineIdToRefresh,
        };
      } catch (e: unknown) {
        const kind = classifySyncItemError(e);
        console.error(
          `[SyncManager] item failed (id=${row.id}, type=${row.typeOfData}, attempt=${attempt}, kind=${kind})`,
          e
        );
        if (kind === 'network' || kind === 'client') {
          return kind === 'network' ? { kind: 'network' } : { kind: 'failed' };
        }
        if (attempt >= MAX_SERVER_ATTEMPTS) {
          return { kind: 'failed' };
        }
        await delay(SERVER_RETRY_DELAY_MS);
        if (!connectivityService.isOnline()) {
          return { kind: 'network' };
        }
      }
    }

    return { kind: 'failed' };
  }
}

export const syncManager = new SyncManager();
